export const cents = (value: number | string) =>
  Math.round(Number(value)) as Cents;
export const dollars = (value: number | string) => Number(value) as Dollars;
export const centsToDollars = (c: Cents) => dollars(c / 100);
export const dollarsToCents = (d: Dollars) => cents(d * 100);
export const sanitizeTextCurrency = (text: string) =>
  text.replace(/[^0-9]/g, ""); // text is pennies, so disallow decimal
export const currencyToCents = (text: string) =>
  cents(sanitizeTextCurrency(text));

// A plain signed decimal amount with optional "$" prefix: "5", "$50",
// "5.25", ".25", "-5.25".
const PLAIN_NUMBER = /^[+-]?\$?(?:\d+(?:\.\d*)?|\.\d+)$/;

// A signed amount with optional "$" prefix and correctly comma-grouped
// thousands: "1,000", "$1,000.50", "$1,000,000", etc.
const GROUPED_NUMBER = /^[+-]?\$?\d{1,3}(?:,\d{3})+(?:\.\d*)?$/;

// Parses free-text amount input (e.g. "$1,000.50", " 25 ", "10.10") into cents.
export const parseAmountInputToCents = (text: string): Cents | null => {
  if (typeof text !== "string") return null;
  const trimmed = text.trim();
  if (!PLAIN_NUMBER.test(trimmed) && !GROUPED_NUMBER.test(trimmed)) {
    return null;
  }
  const stripped = trimmed.replace(/[,$]/g, "");
  const value = Number(stripped);
  if (!Number.isFinite(value)) return null;
  return dollarsToCents(dollars(value));
};

export const zeroDollars = dollars(0);
export const zeroCents = cents(0);

/**
 * Strips an amount down to digits and a decimal point, e.g. "$1,2a3.456" → "123.45".
 * Keeps the first "." and at most 2 decimals, and drops extra leading zeros
 * ("007" → "7", but "0.5" stays "0.5").
 */
const cleanAmountEntry = (text: string) => {
  const [whole, ...decimals] = text.replace(/[^\d.]/g, "").split(".");
  const wholeWithoutLeadingZeros = whole.replace(/^0+(?=\d)/, "");
  if (decimals.length === 0) return wholeWithoutLeadingZeros;
  return `${wholeWithoutLeadingZeros}.${decimals.join("").slice(0, 2)}`;
};

/** "1234567.89" → "1,234,567.89" */
const addCommas = (amount: string) =>
  amount.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * Formats what the user typed or pasted into an amount field, and works out
 * where the caret should go so it stays next to the digit they were editing.
 *
 * @param text - The input's current value.
 * @param caret - The caret position in `text`. Defaults to the end.
 * @returns The formatted value and the caret position in it.
 */
export const formatAmountEntry = (
  text: string,
  caret: number = text.length,
): { value: string; caret: number } => {
  const value = addCommas(cleanAmountEntry(text));

  // Keep the caret next to the digit the user just typed. For example, the
  // field shows "100", the user clicks after the "1" and types "5": `text` is
  // "1500" with the caret after the "5". Formatted, that's "1,500", and the
  // caret should still be after the "5".

  // First count what's in front of the caret: "15", so 2 characters.
  // A zero typed in front of a number ("0|206") is dropped, so don't count it.
  const textBeforeCaret = cleanAmountEntry(text.slice(0, caret));
  const charsBeforeCaret = value.startsWith("0")
    ? textBeforeCaret.length
    : textBeforeCaret.replace(/^0/, "").length;

  // Then move through "1,500" until we've passed 2 characters,
  // skipping the comma: "1", ",", "5" → the caret goes after the "5".
  let newCaret = 0;
  let passed = 0;
  while (passed < charsBeforeCaret && newCaret < value.length) {
    if (value[newCaret] !== ",") passed += 1;
    newCaret += 1;
  }
  return { value, caret: newCaret };
};

// pads a formatted amount entry to 2 decimal places, e.g. "1,000" → "1,000.00"
export const padAmountEntry = (value: string) => {
  if (!value) return "";
  const [whole, fraction = ""] = value.split(".");
  return `${whole || "0"}.${fraction.padEnd(2, "0")}`;
};
